/**
 * Leader Election Module
 *
 * Provides cross-tab leader election using Web Locks API.
 * Only one tab is "leader" at a time and is responsible for
 * token refresh to avoid race conditions.
 *
 * Design:
 * - Uses AbortController to manage lock lifetime
 * - cleanup() aborts the controller, releasing the lock immediately
 *   → fixes React StrictMode deadlock (mount→unmount→remount)
 * - No heartbeats or timers — Web Lock queue handles promotion
 *   automatically when leader tab closes/crashes
 * - Background tab throttling (Chrome 88+) has no effect because
 *   Web Locks are held by the browser process, not the JS event loop
 *
 * Requirements: Web Locks API (Chrome 69+, Feb 2022 for AbortController support)
 *
 * Usage:
 *   const leader = createLeaderElection({
 *     lockName: 'bb_auth0_session_leader',
 *   });
 *
 *   await leader.acquire(() => console.log('I am leader now'));
 *   leader.isLeader(); // true/false
 *   leader.cleanup();  // releases lock, next queued tab promotes
 */

/**
 * Create a leader election controller
 *
 * @param {Object} config
 * @param {string} config.lockName - Web Lock name (unique per provider)
 * @param {string} [config.logPrefix='[Leader]'] - Log prefix for debugging
 * @returns {Object} Leader election controller
 */

import { sessionLog } from './sessionLog';

export const createLeaderElection = ({ lockName, logPrefix = '[Leader]' }) => {
  // Internal state
  let isLeaderFlag = false;
  let controller = null; // AbortController for current lock request
  let onBecomeLeaderCallback = null;
  let onLoseLeadershipCallback = null;

  /**
   * Hold a Web Lock until the AbortController signals.
   * Returns a Promise that resolves when controller.abort() is called.
   *
   * @param {AbortController} ctrl - The controller whose signal releases the lock
   * @returns {Promise<void>}
   */
  const holdLock = (ctrl) =>
    new Promise((resolve) => {
      if (ctrl.signal.aborted) {
        resolve();
        return;
      }
      ctrl.signal.addEventListener('abort', () => resolve(), { once: true });
    });

  /**
   * Internal: request the lock with given options, become leader if granted.
   *
   * @param {AbortController} ctrl
   * @param {Object} lockOptions - Options for navigator.locks.request
   * @returns {Promise<boolean>} True if lock was acquired
   */
  const requestLock = (ctrl, lockOptions = {}) =>
    new Promise((resolve) => {
      if (ctrl.signal.aborted) {
        resolve(false);
        return;
      }

      // Web Locks spec forbids combining `signal` and `ifAvailable`.
      // `signal` is only needed for the queued path (to cancel the queue on cleanup).
      // For `ifAvailable`, the request resolves immediately so there's nothing to cancel.
      const opts = lockOptions.ifAvailable
        ? { ifAvailable: true }
        : { signal: ctrl.signal };

      navigator.locks
        .request(lockName, opts, async (lock) => {
          if (!lock) {
            // ifAvailable: true and lock is held by another tab
            resolve(false);
            return;
          }

          // Got the lock — we are the leader
          isLeaderFlag = true;
          sessionLog(
            `${logPrefix} 👑 Became leader (Web Locks${lockOptions.ifAvailable ? ', immediate' : ', queued'})`
          );
          onBecomeLeaderCallback?.();
          resolve(true);

          // Hold the lock until abort
          await holdLock(ctrl);

          // Lock released — we are no longer leader
          isLeaderFlag = false;
          sessionLog(`${logPrefix} 👥 Released leadership`);
          onLoseLeadershipCallback?.();
        })
        .catch((err) => {
          // AbortError is expected when cleanup() is called while queued
          if (err.name !== 'AbortError') {
            console.warn(`${logPrefix} Web Locks request failed:`, err);
          }
          resolve(false);
        });
    });

  /**
   * Acquire leadership via Web Locks API.
   *
   * 1. If already leader (e.g., React StrictMode remount on module singleton) → callback immediately
   * 2. Try ifAvailable: true for instant acquisition
   * 3. If another tab holds the lock → queue via a second request (no ifAvailable)
   *    that will resolve when the leader releases
   *
   * @param {Function} onBecomeLeader - Callback when this tab becomes leader
   * @param {Function} [onLoseLeadership] - Callback when leadership is lost
   */
  const acquire = async (onBecomeLeader, onLoseLeadership) => {
    onBecomeLeaderCallback = onBecomeLeader;
    onLoseLeadershipCallback = onLoseLeadership;

    // Already leader (module singleton survived React StrictMode remount)
    if (isLeaderFlag) {
      onBecomeLeaderCallback?.();
      return;
    }

    // Abort any previous pending request (cleanup from prior mount)
    if (controller) {
      controller.abort();
    }
    controller = new AbortController();
    const ctrl = controller;

    // Step 1: Try to acquire immediately
    const gotLock = await requestLock(ctrl, { ifAvailable: true });
    if (gotLock) return;

    // Step 2: Queue for the lock — we'll be promoted when the leader releases
    sessionLog(`${logPrefix} 👥 Became follower, queued for promotion`);
    // Fire-and-forget: requestLock will call onBecomeLeader when promoted
    requestLock(ctrl);
  };

  /**
   * Cleanup — releases the Web Lock via AbortController.
   * Safe to call multiple times. Called on React effect cleanup.
   *
   * Unlike the old implementation, this DOES release the lock, which
   * fixes the React StrictMode deadlock. The remounted component calls
   * acquire() again and immediately re-acquires the now-free lock.
   */
  const cleanup = () => {
    if (controller) {
      controller.abort();
      controller = null;
    }
    isLeaderFlag = false;
    onBecomeLeaderCallback = null;
    onLoseLeadershipCallback = null;
  };

  /**
   * Full reset including state clear (call on logout only).
   * Identical to cleanup but explicit about intent.
   */
  const reset = () => {
    cleanup();
  };

  /**
   * No-op — kept for API compatibility with session hooks.
   * The old heartbeat-based system needed message handling;
   * Web Locks don't need cross-tab messages for election.
   *
   * @param {Object} _data - Unused message data
   */
  const handleMessage = (_data) => {
    // No-op: Web Locks handle promotion automatically
  };

  /**
   * Try to claim leadership on-demand without waiting.
   * Used for follower recovery when leader may have died
   * (e.g., leader tab closed, Web Lock is free).
   *
   * @returns {Promise<boolean>} True if leadership was claimed
   */
  const tryClaimLeadership = async () => {
    if (isLeaderFlag) return true;

    // Abort any existing queued request before trying
    if (controller) {
      controller.abort();
    }
    controller = new AbortController();
    const ctrl = controller;

    // Try immediate acquisition
    const gotLock = await requestLock(ctrl, { ifAvailable: true });
    if (gotLock) return true;

    // Lock is still held — leader is alive, re-queue as follower
    requestLock(ctrl);
    return false;
  };

  return {
    acquire,
    cleanup,
    reset,
    handleMessage,
    tryClaimLeadership,
    isLeader: () => isLeaderFlag,
  };
};

export default createLeaderElection;
